import { Injectable } from '@angular/core';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { Router } from '@angular/router';
import { KeplerService } from 'app/kepler/services/kepler.service';
import { tap } from 'rxjs/operators';
import { DEFAULT_RESPONSE_TABLE } from 'app/kepler/assets/constants';

@Injectable()
export class KeplerProgressListService {
  userRole: any;
  constructor(
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public keplerListService: KeplerService,
    public router: Router
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}, match, isToday) {
    const dataSource = new TableDatasource({
      sort: 'position',
      columns: this.getColumns(match, isToday),
      load: (props) => this.fetch(props, dataSource).subscribe(() => dataSource.refresh()),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { matchId, status, round = 'final', ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());

    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      $filter,
      $status: status,
      round
    };
    return this.keplerListService.getMatchRanking(matchId, paginationOptions).pipe(
      tap((response) => {
        dataSource.docs = response?.docs || DEFAULT_RESPONSE_TABLE.docs;
        dataSource.total = response?.total || DEFAULT_RESPONSE_TABLE.total;
        dataSource.limit = response?.limit || DEFAULT_RESPONSE_TABLE.limit;
        dataSource.page = response?.page || DEFAULT_RESPONSE_TABLE.page;
        dataSource.pages = response?.pages || DEFAULT_RESPONSE_TABLE.pages;
      })
    );
  }

  getCustomFilters() {
    return {
      'indicators.co2': (filterBuilder, value) => {
        if (value) return filterBuilder.and(`indicators/co2 ge ${value}`);
      },
      'indicators.population': (filterBuilder, value) => {
        if (value) return filterBuilder.and(`indicators/population ge ${value}`);
      }
    };
  }

  getColumns(match, isToday) {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('nouns.name'),
        dataType: DataTypes.String,
        sortable: true,
        style: { width: '150px' },
        valueParser: (value) => {
          return value || '-';
        }
      },
      {
        source: 'surname',
        label: this.translate.instant('nouns.surname'),
        dataType: DataTypes.String,
        sortable: true,
        style: { width: '150px' },
        valueParser: (value) => {
          return value || '-';
        }
      },
      match.isAnonymous && {
        source: 'alias',
        label: this.translate.instant('nouns.alias'),
        dataType: DataTypes.String,
        sortable: true,
        style: { width: '150px' },
        valueParser: (value) => {
          return value || '-';
        }
      },
      {
        source: 'email',
        label: this.translate.instant('nouns.email'),
        dataType: DataTypes.String,
        sortable: true,
        style: { width: '200px' }
      },
      {
        source: 'indicators.population',
        label: this.translate.instant('kepler.player.header.population'),
        dataType: DataTypes.number,
        sortable: false,
        style: { width: '100px' },
        valueParser: (value) => {
          if (value) return value;
          return '-';
        }
      },
      {
        source: 'indicators.co2',
        label: this.translate.instant('kepler.player.header.polution'),
        dataType: DataTypes.number,
        sortable: false,
        style: { width: '100px' },
        valueParser: (value) => {
          if (value) return Math.round(value);
          return '-';
        }
      },
      {
        source: 'totalKPIPoints',
        label: this.translate.instant('kepler.player.header.kpis'),
        dataType: DataTypes.number,
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: 'totalPoints',
        label: this.translate.instant('kepler.player.header.totalPoints'),
        dataType: DataTypes.number,
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: isToday ? 'firstConnection' : 'matchFirstConnection',
        label: this.translate.instant('kepler.firstConnection'),
        dataType: DataTypes.datetime,
        sortable: false,
        style: { width: '100px' },
        valueParser: (value, player) => {
          // When no specific round is requested, matchFirstConnection is firstConnection from the player
          const matchFirstConnection = value ?? player.firstConnection;
          return matchFirstConnection ? this.listHelper.parseDate(matchFirstConnection, match.timezone) : '-';
        }
      },
      {
        source: isToday ? 'lastConnection' : 'matchLastConnection',
        label: this.translate.instant('kepler.lastConnection'),
        dataType: DataTypes.datetime,
        sortable: false,
        style: { width: '100px' },
        valueParser: (value, player) => {
          // When no specific round is requested, matchLastConnection is lastConnection from the player
          const matchLastConnection = value ?? player.lastConnection;
          return matchLastConnection ? this.listHelper.parseDate(matchLastConnection, match.timezone) : '-';
        }
      },
      {
        source: 'status',
        label: this.translate.instant('nouns.status'),
        style: { width: '200px' },
        valueParser: (value) => {
          const values = {
            suspended: 'kepler.player.status.canceled',
            active: 'kepler.player.status.playing',
            pending: 'kepler.player.status.pending',
            ended: 'kepler.player.status.finished'
          };
          return this.translate.instant(values[value]);
        },
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'pending', value: this.translate.instant('kepler.player.status.pending') },
            { key: 'active', value: this.translate.instant('kepler.player.status.playing') },
            { key: 'ended', value: this.translate.instant('kepler.player.status.finished') },
            { key: 'suspended', value: this.translate.instant('kepler.player.status.canceled') }
          ];
          return renderer`
            <gl-select small multiselect
              .options="${options}"
              .value="${value || 'any'}"
              @onChange="${(e) => callback(e.detail.value)}"
            >
            </gl-select>`;
        }
      }
    ].filter((column) => !!column);
    return columns.filter((col) => col !== null);
  }
}
